import { Tag } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { importedInvoicePaymentStatusToVariant } from '../../constants/importedInvoicePaymentStatusToVariant'
import { useImportedInvoiceDrawer } from '../../context/importedInvoiceDrawer'
import { useCloseUnresolvedImportedInvoiceDrawer } from '../../hooks/useCloseUnresolvedImportedInvoiceDrawer'
import { useFetchImportedInvoiceById } from '../../hooks/useFetchImportedInvoiceById'
import { useImportedInvoiceReviewFeature } from '../../hooks/useImportedInvoiceReviewFeature'
import { isImportedInvoiceApprovable } from '../../utils/isImportedInvoiceApprovable'
import { ImportedInvoiceIntakeStatus, importedInvoiceIntakeStatusToTag } from '../ImportedInvoiceIntakeStatus'
import { ImportedInvoiceDetailPanel } from './elements/ImportedInvoiceDetailPanel'
import { ImportedInvoiceDrawerColumns } from './elements/ImportedInvoiceDrawerColumns'
import { ImportedInvoiceDrawerLayout } from './elements/ImportedInvoiceDrawerLayout'
import { ImportedInvoiceDrawerLoader } from './elements/ImportedInvoiceDrawerLoader'
import { ImportedInvoiceReviewForm } from './elements/ImportedInvoiceReviewForm'

export const ImportedInvoiceDrawer = (): ReactElement | null => {
  const { t } = useTranslation()
  const { isEnabled } = useImportedInvoiceReviewFeature()
  const { importedInvoiceId } = useImportedInvoiceDrawer()
  const { invoice, isLoadingError, isNotFound } = useFetchImportedInvoiceById(importedInvoiceId, { isEnabled })

  // Has to run before the guard below: the drawer stops rendering as soon as the id is dropped.
  useCloseUnresolvedImportedInvoiceDrawer({ isLoadingError, isNotFound })

  if (!isEnabled || !importedInvoiceId) {
    return null
  }

  const approved = invoice?.approved ?? undefined
  const isApproved = Boolean(approved)
  // `paymentStatus` only comes with `approved`, and stays unset until the row has been posted.
  const paymentStatus = approved ? invoice?.paymentStatus : undefined
  const detailVariant = paymentStatus ? importedInvoicePaymentStatusToVariant[paymentStatus] : undefined
  // An approved row can still read `to_review`. Only statuses with a tag get the slot, since an
  // empty one would still push the title aside.
  const hasIntakeStatus = Boolean(invoice && !isApproved && importedInvoiceIntakeStatusToTag[invoice.status])
  const intakeStatus = invoice && hasIntakeStatus ? <ImportedInvoiceIntakeStatus status={invoice.status} /> : undefined
  const status = detailVariant ? (
    <Tag color={detailVariant.tagColor} size="md" type="subtle">
      {t(detailVariant.labelKey)}
    </Tag>
  ) : (
    intakeStatus
  )
  // The review data maps a missing filename to '', so the generic title is what keeps the
  // dialog's accessible name a real string.
  const title = approved?.number || invoice?.fileName || t('imported_invoices.review.title')

  return (
    <ImportedInvoiceDrawerLayout
      hasActions={Boolean(paymentStatus) || isImportedInvoiceApprovable(invoice)}
      status={status}
      title={title}
    >
      {/* A failed fetch keeps the placeholder too — that render is the one the close effect
          above is already leaving. */}
      {invoice ? (
        <ImportedInvoiceDrawerColumns fileId={invoice.fileId}>
          {approved ? (
            <ImportedInvoiceDetailPanel
              approved={approved}
              key={importedInvoiceId}
              paymentStatus={paymentStatus}
              uploadDate={invoice.uploadDate}
            />
          ) : (
            <ImportedInvoiceReviewForm invoice={invoice} key={importedInvoiceId} />
          )}
        </ImportedInvoiceDrawerColumns>
      ) : (
        <ImportedInvoiceDrawerLoader />
      )}
    </ImportedInvoiceDrawerLayout>
  )
}
